import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import useAuth from "../hooks/useAuth";

interface ProtectedRouteProps {
    children: React.ReactNode;
}

/**
 * Requires an authenticated user. Otherwise redirects to sign-in, preserving
 * the intended destination in a `redirect` query param so the user is sent
 * back after logging in.
 */
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { authState } = useAuth();
    const location = useLocation();

    if (!authState.isAuthenticated) {
        const target = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/account/sign-in?redirect=${target}`} replace />;
    }

    return <>{children}</>;
};

/**
 * Requires an authenticated user with the `admin` role. Non-admins who are
 * logged in are sent to their dashboard; logged-out users to sign-in.
 */
export const AdminRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { authState } = useAuth();
    const location = useLocation();

    if (!authState.isAuthenticated) {
        const target = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/account/sign-in?redirect=${target}`} replace />;
    }

    if (!authState.isAdmin) {
        return <Navigate to='/account/dashboard' replace />;
    }

    return <>{children}</>;
};

/**
 * Customer-only pages. Requires an authenticated user who is NOT an admin —
 * admins have no use for the customer flow and are sent to the admin panel.
 */
export const CustomerRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { authState } = useAuth();
    const location = useLocation();

    if (!authState.isAuthenticated) {
        const target = encodeURIComponent(location.pathname + location.search);
        return <Navigate to={`/account/sign-in?redirect=${target}`} replace />;
    }

    if (authState.isAdmin) {
        return <Navigate to='/account/admin' replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
